import { css, Rule } from 'glamor'
import * as React from 'react'
import * as PropTypes from 'prop-types'

import { useTheme } from '../../theme'

import getStyles from './styles'
import { getFlexStyles, getStyleForSize } from './utils'

interface ICommonLayoutProps {
  children?: React.ReactNode
  fill?: boolean
  flush?: boolean
  alignSelf?: 'auto' | 'flex-start' | 'flex-end' | 'center' | 'baseline' | 'stretch'
  alignItems?: 'auto' | 'flex-start' | 'flex-end' | 'center' | 'baseline' | 'stretch'
  alignContent?: 'flex-start' | 'flex-end' | 'center' | 'space-between' | 'space-around' | 'stretch'
  flexDirection?: 'row' | 'row-reverse' | 'column' | 'column-reverse'
  flexWrap?: 'nowrap' | 'wrap' | 'wrap-reverse'
  flex?: string | number
  justifyContent?: 'flex-start' | 'flex-end' | 'center' | 'space-between' | 'space-around'
  order?: number
  style?: Rule
}

const cloneLayoutItems = (items: React.ReactNode, { flushContent }: { flushContent?: boolean }) =>
  React.Children.toArray(items).map((item: any) =>
    item.type === Layout // eslint-disable-line @typescript-eslint/no-use-before-define
      ? item
      : React.cloneElement(item, {
          flush: flushContent,
        })
  )

interface ILayoutItemProps {
  size?: number
  width?: number
  centerContent?: boolean
}

/**
 * Layout.Item
 *
 * @prop {Node} children
 * @prop {bool} fill
 *   - Makes the Layout.Item fill its parent in the parent's flex-direction (i.e. sets flex: 1)
 * @prop {bool} flush
 *   - Removes paddings from the Layout.Item
 * @prop {Number} size
 *   - Defines the width of the Layout.Item
 * @prop {Number} width
 */

export const LayoutItem: React.FC<ILayoutItemProps & ICommonLayoutProps> = ({
  children,
  centerContent,
  fill,
  flush,
  size,
  width,
  alignSelf,
  alignItems,
  alignContent,
  flexDirection,
  flexWrap,
  flex,
  justifyContent,
  order,
  style,
  ...additional
}) => {
  const styles = getStyles(useTheme())

  return (
    <div
      {...css([
        styles.item.base,
        centerContent && styles.item.centerContent,
        fill && styles.item.fill,
        flush && styles.item.flush,
        size && getStyleForSize(size),
        width && getStyleForSize(width),
        getFlexStyles({
          alignSelf,
          alignItems,
          alignContent,
          flexDirection,
          flexWrap,
          flex,
          justifyContent,
          order,
        }),
        style,
      ])}
      {...additional}
    >
      {children}
    </div>
  )
}

LayoutItem.propTypes = {
  children: PropTypes.node,
  size: PropTypes.number,
  width: PropTypes.number,
  centerContent: PropTypes.bool,
  fill: PropTypes.bool,
  flush: PropTypes.bool,
  alignSelf: PropTypes.oneOf(['auto', 'flex-start', 'flex-end', 'center', 'baseline', 'stretch']),
  alignItems: PropTypes.oneOf(['auto', 'flex-start', 'flex-end', 'center', 'baseline', 'stretch']),
  alignContent: PropTypes.oneOf(['flex-start', 'flex-end', 'center', 'space-between', 'space-around', 'stretch']),
  flexDirection: PropTypes.oneOf(['row', 'row-reverse', 'column', 'column-reverse']),
  flexWrap: PropTypes.oneOf(['nowrap', 'wrap', 'wrap-reverse']),
  flex: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  justifyContent: PropTypes.oneOf(['flex-start', 'flex-end', 'center', 'space-between', 'space-around']),
  order: PropTypes.number,
  style: PropTypes.object,
}

interface ILayoutProps {
  main?: boolean
  flushContent?: boolean
}

/**
 * Layout
 *
 * @prop {Node} children
 * @prop {bool} main
 *   - Indicates this is a main content holder, giving it a flex-direction 'column'
 *     and a min-height of 100%
 * @prop {bool} fill
 *   - Makes the Layout fill its parent in the parent's flex-direction (i.e. sets flex: 1)
 * @prop {bool} flush
 *   - Removes margins from the Layout
 * @prop {bool} flushContent
 *   - Removes padding from child Layout.Items
 */

export const Layout: React.FC<ILayoutProps & ICommonLayoutProps> = ({
  children,
  main,
  fill,
  flush,
  flushContent,
  alignSelf,
  alignItems,
  alignContent,
  flexDirection,
  flexWrap,
  flex,
  justifyContent,
  order,
  style,
  ...additional
}) => {
  const styles = getStyles(useTheme())

  return (
    <div
      {...css([
        styles.layout.base,
        main && styles.layout.main,
        fill && styles.layout.fill,
        flush && styles.layout.flush,
        getFlexStyles({
          alignSelf,
          alignItems,
          alignContent,
          flexDirection,
          flexWrap,
          flex,
          justifyContent,
          order,
        }),
        style,
      ])}
      {...additional}
    >
      {cloneLayoutItems(children, { flushContent })}
    </div>
  )
}

Layout.propTypes = {
  children: PropTypes.node,
  main: PropTypes.bool,
  flushContent: PropTypes.bool,
  fill: PropTypes.bool,
  flush: PropTypes.bool,
  alignSelf: PropTypes.oneOf(['auto', 'flex-start', 'flex-end', 'center', 'baseline', 'stretch']),
  alignItems: PropTypes.oneOf(['auto', 'flex-start', 'flex-end', 'center', 'baseline', 'stretch']),
  alignContent: PropTypes.oneOf(['flex-start', 'flex-end', 'center', 'space-between', 'space-around', 'stretch']),
  flexDirection: PropTypes.oneOf(['row', 'row-reverse', 'column', 'column-reverse']),
  flexWrap: PropTypes.oneOf(['nowrap', 'wrap', 'wrap-reverse']),
  flex: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  justifyContent: PropTypes.oneOf(['flex-start', 'flex-end', 'center', 'space-between', 'space-around']),
  order: PropTypes.number,
  style: PropTypes.object,
}
